import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Reveal, SceneHead, routesFor, type Lang } from "../primitives";

const t = {
  es: {
    aria: "Búsqueda e IA",
    label: "Búsqueda e IA",
    title: "Que te encuentren, te comprendan y puedan evaluar tu oferta.",
    intro:
      "Google, los mapas, los asistentes de IA y los marketplaces pueden intervenir en distintos momentos de una misma decisión. No hay una secuencia única que aplique a todos los negocios.",
    cta: "Visibilidad en AI Search",
    surfacesLabel: "Entornos donde puede ocurrir la decisión",
    surfaces: ["Google", "Mapas", "Asistentes de IA", "Marketplaces", "Búsqueda de marca"],
    note:
      "Esquema conceptual del recorrido: descubrir, investigar, comparar y decidir pueden ocurrir en cualquiera de estos entornos y repetirse. No representa datos de tu cuenta ni garantiza que un asistente de IA recomiende tu marca.",
    layers: [
      {
        name: "Visibilidad",
        text: "Que tu contenido sea accesible y que la información relevante esté realmente publicada, no escondida tras scripts, filtros o PDFs.",
      },
      {
        name: "Comprensión",
        text: "Que se entienda qué servicios ofreces, a qué entidades y zonas corresponden, cuál es tu alcance y qué respuestas concretas das.",
      },
      {
        name: "Confianza",
        text: "Experiencia demostrable, casos explicados, fuentes citables y condiciones verificables en el propio sitio.",
      },
    ],
  },
  en: {
    aria: "Search and AI",
    label: "Search and AI",
    title: "Be found, be understood, and let people evaluate your offer.",
    intro:
      "Google, maps, AI assistants and marketplaces can all take part at different moments of the same decision. There is no single sequence that applies to every business.",
    cta: "AI Search visibility",
    surfacesLabel: "Environments where the decision can happen",
    surfaces: ["Google", "Maps", "AI assistants", "Marketplaces", "Branded search"],
    note:
      "Conceptual diagram of the journey: discover, research, compare and decide can happen in any of these environments and repeat. It does not represent data from your account and does not guarantee that an AI assistant will recommend your brand.",
    layers: [
      {
        name: "Visibility",
        text: "Your content is accessible and the relevant information is actually published, not hidden behind scripts, filters or PDFs.",
      },
      {
        name: "Understanding",
        text: "It is clear which services you offer, which entities and areas they relate to, what your scope is and what concrete answers you give.",
      },
      {
        name: "Trust",
        text: "Demonstrable experience, explained cases, citable sources and verifiable conditions on the site itself.",
      },
    ],
  },
} as const;

export const SceneSearchAI = ({ language = "es" as Lang }: { language?: Lang }) => {
  const c = t[language];
  const routes = routesFor(language);

  return (
    <section className="v3-section bg-[color:var(--v3-mineral)]" aria-label={c.aria}>
      <div className="v3-shell">
        <SceneHead index="05" label={c.label} title={c.title} intro={c.intro} />

        <div className="mt-12 grid gap-12 lg:mt-16 lg:grid-cols-12 lg:gap-16">
          <ul className="lg:col-span-6">
            {c.layers.map((l, i) => (
              <Reveal as="li" key={l.name} delay={0.05 * i}>
                <div className="border-t border-[color:var(--v3-line)] py-6">
                  <h3 className="text-[1.15rem] font-semibold">{l.name}</h3>
                  <p className="v3-body mt-3">{l.text}</p>
                </div>
              </Reveal>
            ))}
            <li>
              <Link to={routes.serviceLlm} className="v3-inline-link mt-7">
                {c.cta}
                <ArrowRight className="h-4 w-4" aria-hidden />
              </Link>
            </li>
          </ul>

          <div className="lg:col-span-6">
            <div className="relative bg-white p-7 lg:p-10">
              <svg
                viewBox="0 0 320 240"
                className="pointer-events-none absolute inset-0 h-full w-full"
                aria-hidden
                preserveAspectRatio="none"
              >
                <g stroke="var(--v3-line)" fill="none">
                  <path d="M160 40 V200" />
                  <path d="M40 120 H280" />
                </g>
                <g stroke="var(--v3-blue)" fill="none" strokeWidth="1.2" opacity="0.65">
                  <path d="M160 120 L58 52M160 120 L262 52M160 120 L58 190M160 120 L262 190M160 120 L160 34" />
                </g>
                <circle cx="160" cy="120" r="5" fill="var(--v3-copper)" />
              </svg>

              <p className="v3-micro relative">{c.surfacesLabel}</p>
              <ul className="relative mt-6 grid grid-cols-2 gap-x-6 gap-y-4">
                {c.surfaces.map((s) => (
                  <li key={s} className="text-[0.95rem] font-medium text-[color:var(--v3-ink)]">
                    {s}
                  </li>
                ))}
              </ul>
              <p className="v3-note relative mt-8 border-t border-[color:var(--v3-line)] pt-5">
                {c.note}
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
